import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { DEVICE_TYPES, BLOOD_GROUP_OPTION } from "../../common/constant/common-constants";
import "./PatientDetails.scss";
import ModalBackdrop from "../ModalBackdrop/ModalBackdrop";
import { patienDischargeAction, patientDataAction } from '../../modules/actions/action';
import { admitPatient, dischargePatient } from "../../helper/patient-helper";
import { getHospitalDetails } from "../../helper/home-page-helper";
import { hospitalDataAction } from "../../modules/actions/action";
import CustomCalendar from "../CustomCalendar/CustomCalendar";
import CustomTimePicker from "../CustomTimePicker/CustomTimePicker";
import genderMaleIcon from "../../common/images/gender_male.png";
import genderFemaleIcon from "../../common/images/gender_female.png";
import calendarIcon from "../../common/images/Calendar.png";
import clockIcon from "../../common/images/Clock.png";

const IST_UTC_OFFSET_MINUTES = 330;

/** API stores admissionDate as UTC ISO; display/edit in IST (+5:30). */
const normalizePatientDetailsFromApi = (data) => {
    if (!data || !data.name) return data;

    const next = { ...data };

    if (typeof data.admissionDate === "string" && data.admissionDate.includes("T")) {
        const ist = moment.utc(data.admissionDate).utcOffset(IST_UTC_OFFSET_MINUTES);
        if (ist.isValid()) {
            next.admissionDate = ist.toDate();
            next.admissionTime = ist.format("HH:mm");
        }
    }

    return next;
};

const formatAdmissionDateDisplay = (date) => {
    if (!date) return "";
    return moment(date).format("DD - MM - YYYY");
};

const formatAdmissionTimeDisplay = (timeValue) => {
    if (!timeValue) return "";
    const parsed = moment(timeValue, ["HH:mm", "H:mm"], true);
    if (!parsed.isValid()) return timeValue;
    return parsed.format("HH : mm A");
};

const sanitizeIntegerInput = (raw) => raw.replace(/\D/g, "");

const sanitizeDecimalInput = (raw) => {
    const cleaned = raw.replace(/[^\d.]/g, "");
    const dotIndex = cleaned.indexOf(".");
    if (dotIndex === -1) return cleaned;
    const whole = cleaned.slice(0, dotIndex);
    const fraction = cleaned.slice(dotIndex + 1).replace(/\./g, "");
    return `${whole}.${fraction}`;
};

function PatientDetails(props) {

    const [patientName, setPatientName] = useState("");
    const [patientId, setPatientId] = useState("");
    const [gender, setGender] = useState("");
    const [age, setAge] = useState("");
    const [height, setHeight] = useState("");
    const [weight, setWeight] = useState("");
    const [admissionDate, setAdmissionDate] = useState(new Date());
    const [admissionTime, setAdmissionTime] = useState(moment(new Date()).format("HH:mm"));
    const [bloodGroup, setBloodGroup] = useState("");
    const [diagnosis, setDiagnosis] = useState("");
    const [physician, setPhysician] = useState("");
    const [showDischargeModal, setShowDischargeModal] = useState(false);
    const [isAdmitVisible, setIsAdmitVisible] = useState(false);
    const [patientData, setPatientData] = useState(props.patientData);
    const [errorMsg, setErrorMsg] = useState("");
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [timeOpen, setTimeOpen] = useState(false);

    const dateFieldRef = useRef(null);
    const timeFieldRef = useRef(null);

    const isReadOnly = Boolean(patientData && patientData.name);

    useEffect(() => {
        if (props.patientData && props.patientData.name) {
            setPatientData(normalizePatientDetailsFromApi(props.patientData));
        } else {
            emptyAllFields();
            setPatientData("");
        }
    }, [props.patientData]);

    useEffect(() => {
        setErrorMsg(null);

        if (patientName && gender && age && admissionDate && admissionTime) {
            setIsAdmitVisible(true);
        } else {
            setIsAdmitVisible(false);
        }
    }, [
        patientName,
        patientId,
        gender,
        age,
        height,
        weight,
        admissionDate,
        admissionTime,
        bloodGroup,
        diagnosis,
        physician,
    ]);

    useEffect(() => {
        if (!calendarOpen && !timeOpen) return undefined;

        const handleClickOutside = (event) => {
            if (
                calendarOpen
                && dateFieldRef.current
                && !dateFieldRef.current.contains(event.target)
            ) {
                setCalendarOpen(false);
            }
            if (
                timeOpen
                && timeFieldRef.current
                && !timeFieldRef.current.contains(event.target)
            ) {
                setTimeOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [calendarOpen, timeOpen]);

    const handleSubmitAdmit = () => {
        const data = {
            patientName,
            patientId,
            gender,
            age,
            height,
            weight,
            admissionDate: moment(admissionDate).format("DD-MM-YYYY"),
            admissionTime,
            bloodGroup,
            diagnosis,
            physician,
            bedId: props.bedId,
        };

        admitPatient(data)
            .then((response) => {
                const newData = {
                    bedId: props.bedId,
                    data: {
                        session_id: response.sessionId,
                        name: patientName,
                        patientId,
                        gender,
                        age,
                        height,
                        weight,
                        admissionDate,
                        admissionTime,
                        bloodGroup,
                        diagnosis,
                        physician,
                    },
                };
                props.patientDataAction(newData);
            })
            .catch((err) => {
                setErrorMsg(err);
            });
    };

    const handleSubmitDischarge = () => {
        setShowDischargeModal(true);
    };

    const handleModalCancel = () => {
        setShowDischargeModal(false);
    };

    const emptyAllFields = () => {
        setPatientName("");
        setPatientId("");
        setGender("");
        setAge("");
        setHeight("");
        setWeight("");
        setBloodGroup("");
        setDiagnosis("");
        setPhysician("");
        setAdmissionDate(new Date());
        setAdmissionTime(moment(new Date()).format("HH:mm"));
    };

    const handleModalDischarge = () => {
        if (props.bedId) {
            const data = {
                bedId: props.bedId,
                patientSessionId: props.patientData.session_id,
            };
            dischargePatient(data)
                .then(() => {
                    props.patienDischargeAction({ bedId: props.bedId });
                    getHospitalDetails().then((res) => {
                        if (!res || !res.data || !res.data.wards) return;
                        res.data.wards.map((ward) => {
                            ward.beds.map((bed) => {
                                props.patientDataAction({
                                    bedId: bed.id,
                                    data: bed.patientDetails,
                                });
                                return bed.id;
                            });
                            return ward;
                        });
                    }).catch((err) => {
                        console.log(err);
                    });
                })
                .catch((err) => {
                    setErrorMsg(err);
                });
            setShowDischargeModal(false);
        }
    };

    const resolvedGender = patientData ? patientData.gender : gender;
    const resolvedBloodGroup = patientData ? patientData.bloodGroup : bloodGroup;
    const resolvedAdmissionDate = patientData ? patientData.admissionDate : admissionDate;
    const resolvedAdmissionTime = patientData ? patientData.admissionTime : admissionTime;

    const isGenderDisabled = (value) => {
        if (!isReadOnly) return false;
        return patientData.gender !== value;
    };

    const handleGenderSelect = (value) => {
        if (!isReadOnly) setGender(value);
    };

    const handleBloodGroupSelect = (name) => {
        if (!isReadOnly) setBloodGroup(name);
    };

    const renderSexToggle = (value, label, iconSrc) => {
        const selected = resolvedGender === value;
        const disabled = isGenderDisabled(value);

        return (
            <button
                type="button"
                className={`pd-sex-btn${selected ? " pd-sex-btn--selected" : ""}`}
                disabled={disabled}
                onClick={() => handleGenderSelect(value)}
            >
                <span>{label}</span>

                {iconSrc ? (
                    <img src={iconSrc} alt="" className="pd-sex-btn__icon" aria-hidden="true" />
                ) : null}
            </button>
        );
    };

    return (
        <div className="patient-detail-container">
            <h2 className="patient-details-title">Patient Details</h2>

            <div className="patient-details">
                <div className="pd-row pd-row--name">
                    <div className="pd-field pd-field--grow">
                        <label className="input-label" htmlFor="patient-name">
                            Patient Details<span className="required-field">*</span>
                        </label>
                        <input
                            id="patient-name"
                            className="pd-input pd-input--primary-text"
                            placeholder="Enter Patient Name"
                            value={patientData ? patientData.name : patientName}
                            readOnly={isReadOnly}
                            onChange={(event) => setPatientName(event.target.value)}
                        />
                    </div>
                    <div className="pd-field pd-field--grow">
                        <label className="input-label" htmlFor="patient-id">
                            Patient ID
                        </label>
                        <input
                            id="patient-id"
                            className="pd-input pd-input--primary-text"
                            placeholder="Enter Patient ID"
                            value={patientData ? patientData.patientId : patientId}
                            readOnly={isReadOnly}
                            onChange={(event) => setPatientId(event.target.value)}
                        />
                    </div>
                </div>

                <div className="pd-row pd-row--sex">
                    <div className="pd-field">
                        <div className="input-label">
                            Sex<span className="required-field">*</span>
                        </div>
                        <div className="pd-sex-group">
                            {renderSexToggle("M", "Male", genderMaleIcon)}
                            {renderSexToggle("F", "Female", genderFemaleIcon)}
                            {renderSexToggle("Other", "Other", null)}
                        </div>
                    </div>
                </div>

                <div className="pd-row pd-row--vitals">
                    <div className="pd-field">
                        <div className="input-label">
                            Age<span className="required-field">*</span>
                        </div>
                        {isReadOnly ? (
                            <div className="pd-split-field">
                                <span className="pd-split-field__label">
                                    Age
                                    <br />
                                    (Years)
                                </span>
                                <input
                                    id="patient-age"
                                    className="pd-split-field__value"
                                    value={patientData ? patientData.age : age}
                                    readOnly
                                />
                            </div>
                        ) : (
                            <label className="pd-split-field" htmlFor="patient-age">
                                <span className="pd-split-field__label">
                                    Age
                                    <br />
                                    (Years)
                                </span>
                                <input
                                    id="patient-age"
                                    className="pd-split-field__value"
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={patientData ? patientData.age : age}
                                    onChange={(event) => setAge(sanitizeIntegerInput(event.target.value))}
                                />
                            </label>
                        )}
                    </div>

                    <div className="pd-field">
                        <div className="input-label">
                            Height
                        </div>
                        {isReadOnly ? (
                            <div className="pd-split-field">
                                <span className="pd-split-field__label">
                                    Height
                                    <br />
                                    (cm)
                                </span>
                                <input
                                    id="patient-height"
                                    className="pd-split-field__value"
                                    value={patientData ? patientData.height : height}
                                    readOnly
                                />
                            </div>
                        ) : (
                            <label className="pd-split-field" htmlFor="patient-height">
                                <span className="pd-split-field__label">
                                    Height
                                    <br />
                                    (cm)
                                </span>
                                <input
                                    id="patient-height"
                                    className="pd-split-field__value"
                                    type="text"
                                    inputMode="decimal"
                                    value={patientData ? patientData.height : height}
                                    onChange={(event) => setHeight(sanitizeDecimalInput(event.target.value))}
                                />
                            </label>
                        )}
                    </div>

                    <div className="pd-field">
                        <div className="input-label">
                            Weight
                        </div>
                        {isReadOnly ? (
                            <div className="pd-split-field">
                                <span className="pd-split-field__label">
                                    Weight
                                    <br />
                                    (kg)
                                </span>
                                <input
                                    id="patient-weight"
                                    className="pd-split-field__value"
                                    value={patientData ? patientData.weight : weight}
                                    readOnly
                                />
                            </div>
                        ) : (
                            <label className="pd-split-field" htmlFor="patient-weight">
                                <span className="pd-split-field__label">
                                    Weight
                                    <br />
                                    (kg)
                                </span>
                                <input
                                    id="patient-weight"
                                    className="pd-split-field__value"
                                    type="text"
                                    inputMode="decimal"
                                    value={patientData ? patientData.weight : weight}
                                    onChange={(event) => setWeight(sanitizeDecimalInput(event.target.value))}
                                />
                            </label>
                        )}
                    </div>

                    <div className="pd-field pd-field--date" ref={dateFieldRef}>
                        <label className="input-label">
                            Admission Date<span className="required-field">*</span>
                        </label>
                        <button
                            type="button"
                            className="pd-picker-trigger"
                            disabled={isReadOnly}
                            onClick={() => !isReadOnly && setCalendarOpen((open) => !open)}
                        >
                            <span>{formatAdmissionDateDisplay(resolvedAdmissionDate)}</span>
                            <img src={calendarIcon} alt="" aria-hidden="true" />
                        </button>
                        {calendarOpen && !isReadOnly && (
                            <div className="pd-picker-popover">
                                <CustomCalendar
                                    value={admissionDate}
                                    onChange={(date) => {
                                        setAdmissionDate(date);
                                        setCalendarOpen(false);
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    <div className="pd-field pd-field--time" ref={timeFieldRef}>
                        <label className="input-label">Admission Time</label>
                        <button
                            type="button"
                            className="pd-picker-trigger"
                            disabled={isReadOnly}
                            onClick={() => !isReadOnly && setTimeOpen((open) => !open)}
                        >
                            <span>{formatAdmissionTimeDisplay(resolvedAdmissionTime)}</span>
                            <img src={clockIcon} alt="" aria-hidden="true" />
                        </button>
                        {timeOpen && !isReadOnly && (
                            <div className="pd-picker-popover pd-picker-popover--time">
                                <CustomTimePicker
                                    value={admissionTime}
                                    onChange={setAdmissionTime}
                                />
                            </div>
                        )}
                    </div>

                    <div className="pd-field pd-field--physician">
                        <label className="input-label" htmlFor="patient-physician">
                            Physician
                        </label>
                        <input
                            id="patient-physician"
                            className="pd-input pd-input--primary-text"
                            placeholder="Enter Physician Name"
                            value={patientData ? patientData.physician : physician}
                            readOnly={isReadOnly}
                            onChange={(event) => setPhysician(event.target.value)}
                        />
                    </div>
                </div>

                <div className="pd-row pd-row--blood">
                    <div className="pd-field pd-field--blood">
                        <div className="input-label">Blood Group</div>
                        <div className="pd-blood-group">
                            {BLOOD_GROUP_OPTION.map((option) => {
                                const selected = resolvedBloodGroup === option.name;
                                return (
                                    <button
                                        key={option.id}
                                        type="button"
                                        className={`pd-blood-btn${selected ? " pd-blood-btn--selected" : ""}`}
                                        disabled={isReadOnly && !selected}
                                        onClick={() => handleBloodGroupSelect(option.name)}
                                    >
                                        {option.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="pd-field pd-field--diagnosis">
                        <label className="input-label" htmlFor="patient-diagnosis">
                            Diagnosis
                        </label>
                        <input
                            id="patient-diagnosis"
                            className="pd-input pd-input--primary-text"
                            placeholder="Enter Diagnosis"
                            value={patientData ? patientData.diagnosis : diagnosis}
                            readOnly={isReadOnly}
                            onChange={(event) => setDiagnosis(event.target.value)}
                        />
                    </div>
                </div>

                {errorMsg ? <div className="pd-error-msg">{errorMsg}</div> : null}
            </div>

            <div className="submit-container">
                <div
                    className={
                        patientData
                            ? "discharge submit-button"
                            : (isAdmitVisible ? "submit-button" : "submit-button button-disabled")
                    }
                    onClick={patientData ? handleSubmitDischarge : handleSubmitAdmit}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            if (patientData) handleSubmitDischarge();
                            else if (isAdmitVisible) handleSubmitAdmit();
                        }
                    }}
                >
                    {patientData ? "Discharge Patient" : "Admit Patient"}
                </div>
            </div>

            {showDischargeModal && <ModalBackdrop />}
            {showDischargeModal && (
                <div className="discharge-modal-container">
                    <div className="discharge-content">
                        <span className="discharge-header">Are you sure you want to discharge the patient?</span>
                        <span className="discharge-text">
                            Post discharge you won’t be able to view the patient’s data, so make sure you have recorded the required vitals
                        </span>
                        <div className="discharge-btn-container">
                            <div className="modal-btn cancel" onClick={handleModalCancel}>Cancel</div>
                            <div className="modal-btn discharge" onClick={handleModalDischarge}>Discharge Patient</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const mapStateToProps = (state, ownProps) => {
    const bds = state.bedDetailsReducer.bedDetails
        && state.bedDetailsReducer.bedDetails[ownProps.bedId];

    const pData = state.patientDataReducer.patientData && state.patientDataReducer.patientData[ownProps.bedId];

    const mData = state.deviceVitalDataReducer.deviceVitalData
        && state.deviceVitalDataReducer.deviceVitalData[ownProps.bedId]
        && state.deviceVitalDataReducer.deviceVitalData[ownProps.bedId][DEVICE_TYPES.MONITOR];

    return {
        bedDetails: bds,
        patientData: pData,
        monitorData: mData,
        config: state.configReducer.config.bedDetailConfig,
    };
};

const mapDispatchToProps = (dispatch) => ({
    patienDischargeAction: (payload) => dispatch(patienDischargeAction(payload)),
    patientDataAction: (payload) => dispatch(patientDataAction(payload)),
    hospitalDataAction: (payload) => dispatch(hospitalDataAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PatientDetails);
